import React, { useState } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import * as yup from "yup";
import { toast } from "react-toastify";
import { Form, Group, Input, Heading, LoadingIcon } from "@peracto/peracto-ui";
import { useConfig } from "@peracto/peracto-config";
import { isJson } from "../util";

import { CodeFormatter } from "components/CodeFormatter";
import { CopyButton } from "components/CopyButton";

const schema = yup.object().shape({
    partNumber: yup.string().required("Part Number is required"),
    depot: yup.string().required("Depot is required"),
});

export const GoldlinkProductFinderForm = () => {
    const config = useConfig();
    const API_URL = config.get("api");

    const [loading, setLoading] = useState(false);
    const [requestData, setRequestData] = useState("");
    const [responseData, setResponseData] = useState("");

    const onSubmitForm = async (formData, actions) => {
        setLoading(true);
        setRequestData("");
        setResponseData("");

        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
        };

        try {
            const { data } = await axios.post(
                `${API_URL}/test-harness/goldlink/part-search`,
                {
                    partNumber: formData?.partNumber,
                    depot: formData?.depot,
                },
                {
                    headers,
                },
            );

            setRequestData(data?.request);
            setResponseData(data.response);
        } catch (e) {
            const errorMessage = isJson(
                e?.response?.data?.["hydra:description"],
            )
                ? JSON.parse(e?.response?.data?.["hydra:description"])?.error
                      ?.message
                : e?.response?.data?.["hydra:description"] || e?.message;

            toast.error(errorMessage);
        } finally {
            setLoading(false);
            actions.setSubmitting(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>
                    Goldlink Product Finder | Test Harnesses | Peracto
                </title>
            </Helmet>
            <div className="form-container">
                <Heading name="Goldlink Product Finder" />
                <Form
                    autoComplete="on"
                    values={{
                        partNumber: "",
                        depot: "",
                    }}
                    schema={schema}
                    showActionPanel={false}
                    onSubmit={onSubmitForm}
                >
                    <Group
                        key="request-payload"
                        id="request-payload"
                        name="Request Payload"
                    >
                        <Input
                            name="partNumber"
                            label="Part Number"
                            testId="partNumber"
                            required
                            help="A part number e.g. 'KA-KCC1917'"
                        />

                        <Input
                            name="depot"
                            label="Depot"
                            testId="depot"
                            required
                            help="A depot e.g. 'A'"
                        />

                        <button
                            className="btn btn-primary"
                            type="submit"
                            data-testid="submit"
                        >
                            Submit
                        </button>
                    </Group>

                    {loading && (
                        <div className="flex items-center justify-center py-5">
                            <LoadingIcon />
                        </div>
                    )}

                    {requestData && !loading && (
                        <Group
                            key="request-details"
                            id="request-details"
                            name="Request Details"
                        >
                            <div className="flex items-center justify-between mt-2 mb-3">
                                <h4 className="text-[18px] font-semibold mr-2 mb-0">
                                    Goldlink Product Finder Request
                                </h4>{" "}
                                <CopyButton content={requestData} />
                            </div>
                            <div className="border rounded-[4px] p-2">
                                <CodeFormatter
                                    code={requestData}
                                    language="xml"
                                />
                            </div>
                        </Group>
                    )}

                    {responseData && !loading && (
                        <Group
                            key="response-details"
                            id="response-details"
                            name="Response Details"
                        >
                            <div className="flex items-center justify-between mt-2 mb-3">
                                <h4 className="text-[18px] font-semibold mr-2 mb-0">
                                    Goldlink Product Finder Response
                                </h4>{" "}
                                <CopyButton content={responseData} />
                            </div>
                            <div className="border rounded-[4px] p-2">
                                <CodeFormatter
                                    code={responseData}
                                    language="xml"
                                />
                            </div>
                        </Group>
                    )}
                </Form>
            </div>
        </>
    );
};

GoldlinkProductFinderForm.displayName = "GoldlinkProductFinderForm";
